import React from 'react';
import { Briefcase } from 'lucide-react';
import LoanPageTemplate from '../../components/LoanPageTemplate';

const CommercialLoans = () => (
  <LoanPageTemplate
    icon={Briefcase}
    path="/commercial-loans"
    title="Commercial Loans"
    subtitle="Finance your business property, expansion, or development project. We structure commercial loans to suit your business cash flow and goals."
    description="Commercial lending in Australia is fundamentally different from residential finance. Lenders assess your business financials, industry risk, property type, and lease profile before determining your rate and terms. With loan-to-value ratios typically capped at 65-70% and rates that vary significantly between lenders, having a broker who understands commercial credit is essential. We work with major banks, non-bank lenders, and private funders to structure the right facility for your business."
    features={[
      {
        title: 'Commercial Property Purchase',
        description: 'Finance retail, office, industrial, and mixed-use properties with LVRs up to 70%. We source competitive rates from lenders who specialise in your property type and location.'
      },
      {
        title: 'Development Finance',
        description: 'Fund residential and commercial construction projects with staged drawdown facilities. We work with lenders experienced in development risk including pre-sales requirements and cost overrun provisions.'
      },
      {
        title: 'Business Acquisition Finance',
        description: 'Purchase an existing business or franchise with a structured finance package covering goodwill, stock, equipment, and property. We present your acquisition case to lenders who understand business valuations.'
      },
      {
        title: 'Working Capital & Overdraft Facilities',
        description: 'Maintain cash flow with revolving credit lines, overdrafts, and invoice finance facilities. We match the right product to your cash flow cycle and seasonal requirements.'
      },
      {
        title: 'SMSF Commercial Property',
        description: 'Your SMSF can purchase commercial property and lease it to your own business at market rent. We arrange compliant SMSF commercial lending with competitive terms and guide you through the trustee requirements.'
      },
      {
        title: 'Refinancing Existing Commercial Debt',
        description: 'Commercial loan rates are rarely reviewed automatically. We audit your existing facilities, renegotiate with your current lender, and refinance to a better deal if they will not match the market.'
      }
    ]}
    process={[
      {
        title: 'Business & Financial Review',
        description: 'We review your business financials, tax returns, and existing facilities to understand your position. For property purchases, we also assess the tenancy profile and lease terms.'
      },
      {
        title: 'Facility Structuring',
        description: 'Commercial loans can be structured as term loans, interest-only facilities, lines of credit, or a combination. We design the structure that aligns with your cash flow and tax strategy.'
      },
      {
        title: 'Lender Matching & Negotiation',
        description: 'We prepare a credit submission tailored to each lender\'s appetite and negotiate the best combination of rate, LVR, and covenants. Commercial lending is highly negotiable and we leverage that.'
      },
      {
        title: 'Approval, Valuation & Settlement',
        description: 'We manage the commercial valuation process, satisfy all conditions precedent, and coordinate settlement with solicitors, valuers, and the lender\'s credit team.'
      }
    ]}
    faqs={[
      {
        q: 'How much deposit do I need for a commercial property?',
        a: 'Most commercial lenders require a 30-35% deposit (65-70% LVR). Some specialist lenders will go to 80% LVR for owner-occupied commercial premises with strong financials. SMSF commercial purchases typically require a 30% deposit minimum. We assess your situation and find the best available terms.'
      },
      {
        q: 'What interest rates apply to commercial loans?',
        a: 'Commercial rates are typically 0.50-2.00% higher than residential rates and vary based on property type, LVR, loan size, and your business financials. A standard owner-occupied commercial property might attract a rate of 6.50-7.50%, while specialised or higher-risk properties may be 8.00%+. We negotiate aggressively to secure the best rate for your profile.'
      },
      {
        q: 'Can I purchase my business premises through my SMSF?',
        a: 'Yes. Your SMSF can purchase commercial property and lease it back to your business at market rent, which is fully tax-deductible to the business. The rental income flows into your super fund at the concessional 15% tax rate. The property must be held solely for retirement benefit purposes and meet all ATO compliance requirements. We arrange compliant SMSF lending and work with your accountant on the structure.'
      },
      {
        q: 'What documents do I need for a commercial loan application?',
        a: 'Lenders typically require two years of business and personal tax returns, current financial statements, a business plan or cash flow forecast, details of the property including lease agreements, and a statement of assets and liabilities. For development finance, you will also need DA approval, quantity surveyor reports, and builder contracts. We provide a tailored checklist based on your specific scenario.'
      },
      {
        q: 'What is the difference between a commercial loan and a business loan?',
        a: 'A commercial loan is secured against commercial property (office, retail, industrial, or development land). A business loan is a broader term that includes unsecured facilities, equipment finance, overdrafts, and invoice financing. Commercial property loans typically offer lower rates due to the property security, while unsecured business loans carry higher rates but do not require real estate as collateral.'
      }
    ]}
    loanType="Commercial Loan"
    color="from-indigo-600 to-indigo-800"
  />
);

export default CommercialLoans;
